<template>
  <header class="hub-nav" :class="{ 'is-floating': isFloating }" @keydown.esc="dismissMenus">
    <div class="hub-nav__brand"><Logo /></div>

    <nav
      id="hub-navigation"
      class="hub-nav__navigation"
      :class="{ 'is-open': menuOpen }"
      :aria-label="$t('header.navigation')"
    >
      <ul class="hub-nav__links">
        <li class="hub-nav__dropdown" :class="{ 'is-expanded': sectionsOpen }">
          <div class="hub-nav__dropdown-link">
            <router-link exact to="/" aria-haspopup="true" @click.native="dismissMenus">
              {{ $t('header.home.title') }}
              <Icon class="hub-nav__caret" name="chevron-down" size="16" />
            </router-link>
            <!-- Touch/mobile toggle; on desktop the menu opens on hover/focus -->
            <button
              type="button"
              class="hub-nav__chevron"
              :aria-label="$t('header.homeSections')"
              :aria-expanded="String(sectionsOpen)"
              aria-controls="hub-home-sections"
              @click="sectionsOpen = !sectionsOpen"
            >
              <Icon name="chevron-down" size="16" />
            </button>
          </div>
          <ul id="hub-home-sections" class="hub-nav__submenu">
            <li v-for="(section, index) in sections" :key="section">
              <a :href="'/#section' + (index + 1)" @click.prevent="goToSection(index + 1)">
                {{ $t('header.home.' + section) }}
              </a>
            </li>
          </ul>
        </li>
        <li v-for="link in links" :key="link.path">
          <router-link :to="link.path" @click.native="dismissMenus">
            {{ $t('header.' + link.label) }}
          </router-link>
        </li>
      </ul>
    </nav>

    <div class="hub-nav__actions">
      <button
        type="button"
        class="btn-default btn-small round btn-disabled hub-nav__package"
        :title="$t('header.comingSoon')"
        disabled
      >
        {{ $t('header.package') }}
      </button>
      <button
        v-if="showThemeSwitcher"
        type="button"
        class="hub-nav__icon-button"
        :aria-label="$t(isLight ? 'header.switchToDark' : 'header.switchToLight')"
        @click="$store.commit('dataLogin', !isLight)"
      >
        <Icon :name="isLight ? 'moon' : 'sun'" size="22" />
      </button>
      <div class="hub-nav__language">
        <button
          ref="languageButton"
          type="button"
          class="hub-nav__language-button"
          :aria-label="$t('header.language')"
          aria-haspopup="dialog"
          @click="openLanguage"
        >
          <Icon name="globe" size="20" />
          <span>{{ $i18n.locale.toUpperCase() }}</span>
        </button>
      </div>
      <button
        ref="menuButton"
        type="button"
        class="hub-nav__icon-button hub-nav__menu-toggle"
        :aria-label="$t(menuOpen ? 'header.closeMenu' : 'header.openMenu')"
        :aria-expanded="String(menuOpen)"
        aria-controls="hub-navigation"
        @click="menuOpen = !menuOpen"
      >
        <Icon :name="menuOpen ? 'x' : 'menu'" size="22" />
      </button>
    </div>
  </header>
</template>

<script>
import Logo from '../../elements/logo/Logo'
import Icon from '../../icon/Icon'

export default {
  name: 'FloatingHeader',
  components: { Logo, Icon },
  props: {
    showThemeSwitcher: { type: Boolean, default: true }
  },
  data() {
    return {
      menuOpen: false,
      isFloating: false,
      sectionsOpen: false,
      sections: [
        'hub_things',
        'benefits_of_ev_roaming',
        'service',
        'concern',
        'feature',
        'asean',
        'customers',
        'steps',
        'help'
      ],
      links: [
        { path: '/Blog', label: 'blog' },
        { path: '/Aboutnew', label: 'about' },
        { path: '/contact', label: 'contact' }
      ]
    }
  },
  computed: {
    isLight() {
      return this.$store.state.userLogin
    }
  },
  watch: {
    $route() {
      this.dismissMenus()
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
    window.addEventListener('scroll', this.updateFloating, { passive: true })
    this.updateFloating()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('scroll', this.updateFloating)
  },
  methods: {
    updateFloating() {
      this.isFloating = window.pageYOffset > 24
    },
    dismissMenus(event) {
      if (event && event.key === 'Escape') {
        if (this.menuOpen) this.$refs.menuButton.focus()
        else if (this.sectionsOpen) this.$el.querySelector('.hub-nav__chevron').focus()
      }
      this.menuOpen = false
      this.sectionsOpen = false
    },
    handleOutsideClick(event) {
      if (!event.composedPath().includes(this.$el)) this.dismissMenus()
    },
    openLanguage() {
      this.dismissMenus()
      this.$emit('open-language')
    },
    focusLanguageButton() {
      this.$refs.languageButton.focus()
    },
    goToSection(number) {
      const target = 'section' + number
      this.dismissMenus()
      // Drop focus so the hover/focus-driven submenu closes after picking a section
      if (document.activeElement) document.activeElement.blur()
      if (this.$route.path !== '/') {
        this.$store.commit('setScrollTarget', target)
        this.$router.push('/')
      } else {
        const element = document.getElementById(target)
        if (element) {
          const top = element.getBoundingClientRect().top + window.pageYOffset - 120
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          window.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' })
        }
      }
    }
  }
}
</script>
